/**
 * Analyze tab viewport overlays.
 *
 * - AnalyticalModelOverlay: blue member axis lines, node spheres and support
 *   symbols (triangle/circle/roller) from analyticalModelManager, refreshed
 *   via the analysis store version counter.
 * - ResultsColorFillOverlay: per-member force diagrams built with
 *   ResultsVisualization from the last FEA run (BIM "Results and Color
 *   Fill"), drawn as offset polylines with peak value labels.
 *
 * Engineering overlays use fixed, non-theme colors on purpose — same as
 * BIM, where analytical/analysis display colors are standardized.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useAnalysisStore } from '../state/useAnalysisStore';
import { analyticalModelManager, resultsVisualization } from '../core/services';
import type { AnalysisResults } from '../core/FEASolver';
import { BoundaryCondition } from '../core/AnalyticalModelManager';

const ANALYTICAL_LINE_COLOR = '#2f9bff';
const NODE_COLORS: Record<BoundaryCondition, string> = {
    Fixed: '#d23f31',
    Pinned: '#e2622e',
    Roller: '#f2c230',
    Spring: '#9b59d0',
    Free: '#8aa0b8',
};

export function AnalyticalModelOverlay() {
    const show = useAnalysisStore((s) => s.showAnalyticalModel);
    const version = useAnalysisStore((s) => s.analyticalVersion);

    const data = useMemo(() => {
        if (!show) return null;
        // version participates so the memo recomputes after analytical edits.
        void version;
        const nodes = analyticalModelManager.getNodes();
        const members = analyticalModelManager.getMembers();
        const nodeById = new Map(nodes.map(n => [n.id, n]));
        const memberLines: { key: string; points: THREE.Vector3[] }[] = [];
        members.forEach(m => {
            const s = nodeById.get(m.startNodeId);
            const e = nodeById.get(m.endNodeId);
            if (!s || !e) return;
            memberLines.push({
                key: m.id,
                points: [
                    new THREE.Vector3(...s.position),
                    new THREE.Vector3(...e.position),
                ],
            });
        });
        const supported = nodes.filter(n => n.boundaryCondition !== 'Free');
        return { nodes, memberLines, supported };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, version]);

    if (!data) return null;

    return (
        <group>
            {data.memberLines.map(line => (
                <Line
                    key={line.key}
                    points={line.points}
                    color={ANALYTICAL_LINE_COLOR}
                    lineWidth={2}
                    transparent
                    opacity={0.9}
                />
            ))}
            {data.nodes.map(node => (
                <mesh key={node.id} position={node.position}>
                    <sphereGeometry args={[0.06, 10, 10]} />
                    <meshBasicMaterial color={NODE_COLORS.Free} />
                </mesh>
            ))}
            {data.supported.map(node => (
                <SupportSymbol key={node.id} position={node.position} condition={node.boundaryCondition} />
            ))}
        </group>
    );
}

function SupportSymbol({ position, condition }: { position: [number, number, number]; condition: BoundaryCondition }) {
    const color = NODE_COLORS[condition];
    // Small triangle below the node; roller adds a circle, fixed adds hatching.
    const tri = useMemo(() => {
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-0.18, -0.3);
        shape.lineTo(0.18, -0.3);
        shape.closePath();
        return shape;
    }, []);
    return (
        <group position={position}>
            <mesh position={[0, 0, -0.02]}>
                <extrudeGeometry args={[tri, { depth: 0.02, bevelEnabled: false }]} />
                <meshBasicMaterial color={color} transparent opacity={0.9} />
            </mesh>
            {condition === 'Roller' && (
                <mesh position={[0, -0.38, 0]}>
                    <sphereGeometry args={[0.06, 10, 10]} />
                    <meshBasicMaterial color={color} />
                </mesh>
            )}
            {condition === 'Fixed' && (
                <Line
                    points={[
                        [-0.28, -0.32, 0],
                        [0.28, -0.32, 0],
                    ]}
                    color={color}
                    lineWidth={2}
                />
            )}
            {condition === 'Spring' && (
                <Line
                    points={[
                        [0, -0.3, 0], [0.08, -0.38, 0], [-0.08, -0.46, 0], [0.08, -0.54, 0], [0, -0.62, 0],
                    ]}
                    color={color}
                    lineWidth={1.5}
                />
            )}
        </group>
    );
}

/**
 * Member force diagrams for the last analysis run, scaled and offset
 * perpendicular to each member like BIM's moment diagrams.
 */
export function ResultsColorFillOverlay() {
    const show = useAnalysisStore((s) => s.showResults);
    const results = useAnalysisStore((s) => s.lastFeaResults) as AnalysisResults | null;

    const diagrams = useMemo(() => {
        if (!show || !results) return [];
        const nodes = analyticalModelManager.getNodes();
        const members = analyticalModelManager.getMembers();
        const nodeById = new Map(nodes.map(n => [n.id, n]));
        const positions = new Map<string, { start: THREE.Vector3; end: THREE.Vector3 }>();
        members.forEach(m => {
            const s = nodeById.get(m.startNodeId);
            const e = nodeById.get(m.endNodeId);
            if (s && e) {
                positions.set(m.id, {
                    start: new THREE.Vector3(...s.position),
                    end: new THREE.Vector3(...e.position),
                });
            }
        });
        return resultsVisualization.generateForceDiagram(results, positions);
    }, [show, results]);

    if (!show || !results) return null;

    return (
        <group>
            {diagrams.map(d => {
                if (d.positions.length < 2) return null;
                const peak = d.values.reduce((a, v) => (Math.abs(v) > Math.abs(a) ? v : a), 0);
                return (
                    <group key={d.memberId}>
                        <Line
                            points={d.positions}
                            vertexColors={d.colors.map(c => [c.r, c.g, c.b])}
                            lineWidth={2.5}
                        />
                        <Text
                            position={d.positions[Math.max(0, Math.floor(d.positions.length / 2))].clone().add(new THREE.Vector3(0, 0.35, 0))}
                            fontSize={0.28}
                            color="#d23f31"
                            anchorX="center"
                            anchorY="bottom"
                        >
                            {`${(peak / 1000).toFixed(1)} kN·m`}
                        </Text>
                    </group>
                );
            })}
        </group>
    );
}
